import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Sparkles, Rocket, Palette, Globe, Wand2, Camera,
  Layout, Inbox, TrendingUp, FileUp, ShieldCheck, Workflow, BarChart3, Lock,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SectionInfographic from "@/components/SectionInfographic";

/* Canonical Studio feature catalogue mirrored from the backend infographics
   FEATURE_CATALOG. Editing the backend alone is the source of truth — this
   file only holds the client-side copy (H1, sub-heading, story blocks). */
export const STUDIO_FEATURES = {
  "ad-growth-studio": {
    title: "AI Ad & Growth Studio",
    tagline: "One workflow: brand DNA → campaign → landing page → ads → leads",
    Icon: Sparkles, accent: "indigo",
    story: [
      "Zeely-inspired, WebsiteTech-native. The Studio replaces the sprawl of disconnected AI generators with a single guided workflow.",
      "You start with an 8-question growth onboarding, publish a Brand DNA record every downstream AI reads before it speaks, then turn any URL, product or brief into a full campaign kit — copy, static ads, landing page, tracking links.",
      "Nothing ships without a human tap in the Approvals inbox, and the AI Growth Manager sends plain-language recommendations every week.",
    ],
    cta: { label: "Open the Studio", href: "/studio" },
    subfeatures: ["brand-dna", "page-to-campaign", "ai-copywriter",
                    "static-ad-studio", "landing-page-generator",
                    "approvals-inbox", "growth-manager", "bulk-catalogue-import"],
  },
  "brand-dna": {
    title: "Business & Brand DNA",
    tagline: "One authoritative brand record every AI reads before it speaks",
    Icon: Palette, accent: "violet",
    story: [
      "Logos, colours, typography, tone, glossary, permitted vs prohibited claims, disclaimers, testimonials, and destinations — all in one versioned record.",
      "Every AI-generated asset in the platform stamps the exact Brand DNA version it was built from, so audits are trivial and rollbacks are one click.",
    ],
    cta: { label: "Edit your Brand DNA", href: "/studio" },
  },
  "page-to-campaign": {
    title: "Page-to-Campaign",
    tagline: "Any URL becomes a structured campaign brief in one paste",
    Icon: Globe, accent: "emerald",
    story: [
      "Paste a product, service or menu URL. The Page-to-Campaign agent fetches it, strips scripts and styles, then hands the trimmed body to Claude Sonnet for STRICT-JSON extraction.",
      "You get a structured brief with title, description, price, benefits, proof, CTA hint, audience, images and legal-restriction flags — ready for the AI Copywriter.",
    ],
    cta: { label: "Import a URL", href: "/studio" },
  },
  "ai-copywriter": {
    title: "AI Copywriter",
    tagline: "11 marketing frameworks × 9 tones × your Brand DNA",
    Icon: Wand2, accent: "indigo",
    story: [
      "Pick a framework (AIDA, PAS, Before-After-Bridge, FAB, 4U, Problem-Solution-Proof and more), pick a tone, pick a length. Get 1-5 variants with headline, body, CTA and a why-it-works line.",
      "Every variant is auto-scanned against your Brand DNA's prohibited claims + restricted words and flagged before it can ship. Lock the ones you love — publications will only run against locked copy.",
    ],
    cta: { label: "Generate copy", href: "/studio" },
  },
  "static-ad-studio": {
    title: "Static Ad Studio",
    tagline: "Turn a locked copy variant into on-brand image ads in every size",
    Icon: Camera, accent: "pink",
    story: [
      "Powered by Gemini Nano Banana. The Static Ad Studio ingests your locked copy variant, layers in the Brand DNA's primary/secondary/accent hex colours and heading typography, and renders 1:1, 4:5, 9:16 and 16:9 ads in one run.",
      "Failed renders come back as a placeholder with no credit charge — retry is free. Every asset routes through Approvals before publishing.",
    ],
    cta: { label: "Generate ads", href: "/studio" },
  },
  "landing-page-generator": {
    title: "Campaign Landing Page Generator",
    tagline: "Every campaign ships with a matching page — not a hope",
    Icon: Layout, accent: "sky",
    story: [
      "One click builds a mobile-first HTML landing page whose headline, body and CTA come from your locked copy variant.",
      "Brand colours, hero image, disclaimers and auto-linked contact channels are baked in. Preview inline, share the URL, or map a custom subdomain so the page reads as your own brand.",
    ],
    cta: { label: "Generate a landing page", href: "/studio" },
  },
  "approvals-inbox": {
    title: "Approvals Inbox",
    tagline: "Nothing ships without a human tap — every draft in one place",
    Icon: Inbox, accent: "amber",
    story: [
      "Every AI-generated copy variant, ad and landing page auto-creates a pending approval row. Approvers see them all in one filtered inbox with Pending / Approved / Rejected / Changes tabs.",
      "Decisions propagate to the target's approved flag and stamp an immutable audit event. This is how the Studio stays compliant with your Section I rules.",
    ],
    cta: { label: "Open Approvals", href: "/studio" },
  },
  "growth-manager": {
    title: "AI Growth Manager",
    tagline: "Plain-language recommendations every week — advisory, never autopilot",
    Icon: TrendingUp, accent: "emerald",
    story: [
      "The Growth Manager reads your publications, spend, ROAS and Brand DNA, then produces 3-5 recommendations tagged with confidence and category (creative fatigue, budget shift, new variant, landing page, tracking, channel mix).",
      "Every recommendation is advisory. Autonomous spend is deferred until attribution has been proven reliable — you decide what to act on.",
    ],
    cta: { label: "Get recommendations", href: "/studio" },
  },
  "bulk-catalogue-import": {
    title: "Bulk Catalogue Import",
    tagline: "Upload a CSV of URLs — your whole catalogue as campaign briefs by morning",
    Icon: FileUp, accent: "cyan",
    story: [
      "Drop a CSV whose first column is URLs. A background job runs Page-to-Campaign on up to 200 rows, streaming a live progress bar.",
      "Runs out of credits mid-batch? The job pauses gracefully with a clear per-URL error log — no silent failures, no wasted work.",
    ],
    cta: { label: "Import a catalogue", href: "/studio" },
  },
};

const STUDIO_FAMILY_ORDER = [
  "ad-growth-studio", "brand-dna", "page-to-campaign", "ai-copywriter",
  "static-ad-studio", "landing-page-generator", "approvals-inbox",
  "growth-manager", "bulk-catalogue-import",
];

/* Section framing — the same 4 for every feature page.
 * Each section renders its own AI-generated infographic + narrative + bullets.
 * Bullets are pulled from the feature's shipped capability list on the
 * BACKEND (FEATURE_CATALOG); we intentionally re-frame them here without
 * duplicating copy — the section infographic's LLM prompt (server-side)
 * uses the same list plus a section-specific visual style.
 */
const SECTION_ORDER = ["overview", "how-it-works", "outcomes", "guardrails"];
const SECTION_META = {
  "overview": {
    Icon: Sparkles,
    eyebrow: "Overview",
    heading: (feat) => `What ${feat.title} really does`,
    subheading: (feat) => feat.tagline,
    prose: (feat) => [
      feat.story[0],
      "The overview panel below is an AI-generated isometric infographic — every card is grounded in a real, shipped capability of this module. No mock-ups, no lorem ipsum.",
    ],
    bullets: [
      "One workflow — not another tab to remember",
      "Zero setup — inherits your Brand DNA on day one",
      "Every AI output stamped with its DNA version for audit",
      "Consent, disclosure and rate-limits are on by default",
    ],
  },
  "how-it-works": {
    Icon: Workflow,
    eyebrow: "How it works",
    heading: () => "A guided 4-step workflow",
    subheading: () => "Prepare · Configure · Run · Approve — nothing ships until a human taps yes.",
    prose: (feat) => [
      "Every AI action in the Studio follows the same shape: fetch inputs, ground them against your brand, generate variants, then park them in Approvals.",
      "The workflow infographic below is regenerated from this module's shipped capabilities so it always tells the truth about what runs end-to-end.",
    ],
    bullets: [
      "Prepare — inputs come from your DNA, URLs, catalogue or ad-hoc briefs",
      "Configure — pick frameworks, tones, sizes and audiences",
      "Run — the LLM drafts; credits are checked before spend",
      "Approve — every draft becomes a pending row for a human to sign off",
    ],
  },
  "outcomes": {
    Icon: BarChart3,
    eyebrow: "Outcomes",
    heading: () => "Speed, coverage and cost — measurable from day one",
    subheading: () => "The numbers change the moment your Brand DNA is published.",
    prose: (feat) => [
      "Teams typically see minutes-not-days turnarounds on campaign kits, dramatically wider coverage per week, and clean attribution back to the assets that actually converted.",
      "The stat-card infographic below is a stylised, editorial visualisation of the outcomes this module unlocks — grounded in the capability list your customers actually see.",
    ],
    bullets: [
      "Minutes, not days — a full campaign kit in one sitting",
      "10× coverage — brief 10 products in the time it took to brief 1",
      "Lower cost-per-lead — locked variants + attribution beat guesswork",
      "Consistent brand — every asset uses the same DNA snapshot",
    ],
  },
  "guardrails": {
    Icon: Lock,
    eyebrow: "Guardrails & controls",
    heading: () => "Safe, consent-first, and human-approved",
    subheading: () => "Every AI action in this module is bounded, auditable and reversible.",
    prose: (feat) => [
      "Prohibited claims and restricted words are blocked at generation time; PII is redacted before storage; every approval and rejection stamps an immutable audit row.",
      "The trust panel below visualises the specific policies this module enforces — role scope, consent, rate limits and disclosure.",
    ],
    bullets: [
      "Human-in-the-loop — nothing publishes without a tap",
      "Immutable audit trail on every generation and decision",
      "Prohibited claims + restricted words auto-flagged",
      "Credit guardrail — the LLM refuses to run when balance is out",
    ],
  },
};

const ACCENT_MAP = {
  indigo:  { text: "text-indigo-700",  bg: "bg-indigo-50",  ring: "ring-indigo-500/15",  grad: "from-indigo-600 via-fuchsia-500 to-orange-500" },
  violet:  { text: "text-violet-700",  bg: "bg-violet-50",  ring: "ring-violet-500/15",  grad: "from-violet-600 via-fuchsia-500 to-pink-500" },
  emerald: { text: "text-emerald-700", bg: "bg-emerald-50", ring: "ring-emerald-500/15", grad: "from-emerald-600 via-teal-500 to-cyan-500" },
  pink:    { text: "text-pink-700",    bg: "bg-pink-50",    ring: "ring-pink-500/15",    grad: "from-pink-600 via-fuchsia-500 to-orange-500" },
  sky:     { text: "text-sky-700",     bg: "bg-sky-50",     ring: "ring-sky-500/15",     grad: "from-sky-600 via-cyan-500 to-emerald-500" },
  amber:   { text: "text-amber-700",   bg: "bg-amber-50",   ring: "ring-amber-500/15",   grad: "from-amber-500 via-orange-500 to-rose-500" },
  cyan:    { text: "text-cyan-700",    bg: "bg-cyan-50",    ring: "ring-cyan-500/15",    grad: "from-cyan-600 via-teal-500 to-emerald-500" },
};

/* ==========================================================================
   Router entry — /solutions/studio/:slug renders any Studio feature page.
   ========================================================================== */
export default function StudioFeaturePage() {
  const { slug } = useParams();
  const feat = STUDIO_FEATURES[slug];
  const accent = ACCENT_MAP[feat?.accent] || ACCENT_MAP.indigo;
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const infoUrl = `${BACKEND}/api/infographics/${slug}.png`;
  const canonical = `https://web-transform-57.preview.emergentagent.com/solutions/studio/${slug}`;

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    if (!feat) return;
    document.title = `${feat.title} · WebsiteTech`;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.rel = rel; document.head.appendChild(el); }
      el.href = href;
    };
    setMeta("description", feat.tagline);
    setLink("canonical", canonical);
    document.querySelectorAll('script[data-studio-ld]').forEach((s) => s.remove());
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": feat.title,
      "description": feat.tagline,
      "brand": { "@type": "Brand", "name": "WebsiteTech.pro" },
      "image": infoUrl,
      "url": canonical,
      "category": "AI-powered marketing tool",
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "priceCurrency": "INR",
        "price": "0",
        "description": "Included in the WebsiteTech AI Ad & Growth Studio",
      },
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.dataset.studioLd = "1";
    s.text = JSON.stringify(jsonld);
    document.head.appendChild(s);
  }, [slug]); // eslint-disable-line

  if (!feat) return <Navigate to="/features" replace />;

  const isStudioHome = slug === "ad-growth-studio";
  const subfeatures = feat.subfeatures || [];

  return (
    <div className="min-h-screen bg-[#fdfbf5] text-slate-900" data-testid={`studio-feature-${slug}`}>
      <Navbar />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-10 items-center">
          <div>
            <Link to="/features" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900 font-bold">
              ← All features
            </Link>
            <div className={`mt-4 inline-flex items-center gap-2 rounded-full ${accent.bg} ${accent.text} px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold ring-1 ${accent.ring}`}>
              <feat.Icon className="w-3.5 h-3.5" /> Studio family
            </div>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              {feat.title}
            </h1>
            <div className={`mt-4 text-lg ${accent.text} font-semibold`}>{feat.tagline}</div>
            <div className="mt-6 space-y-3 text-slate-700">
              {feat.story.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <Link to={feat.cta.href}
                data-testid={`studio-feature-cta-${slug}`}
                className={`inline-flex items-center gap-2 rounded-lg bg-gradient-to-r ${accent.grad} text-white px-6 py-3 text-sm font-bold shadow-lg hover:opacity-95 transition`}>
                {feat.cta.label} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/features"
                className="inline-flex items-center gap-2 rounded-lg border border-[#dcd3bc] hover:border-[#c8bd9c] bg-white hover:bg-[#f5efdd] px-5 py-3 text-sm font-semibold text-slate-800">
                Explore other features
              </Link>
            </div>
          </div>
          <div>
            <div className={`rounded-3xl overflow-hidden border border-[#ebe4d3] bg-white shadow-2xl ring-1 ${accent.ring}`}>
              <img src={infoUrl} alt={`${feat.title} infographic`}
                   loading="lazy"
                   className="w-full h-auto"
                   onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            </div>
            <div className="mt-3 text-[11px] text-slate-500 text-center">
              AI-generated infographic · updates automatically as features evolve
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================
          4 AI-infographic sections — one per section, all grounded
          in this feature's real capability list. Alternates layout.
         ========================================================== */}
      {SECTION_ORDER.map((sectionKey, idx) => {
        const s = SECTION_META[sectionKey];
        const rightSideImage = idx % 2 === 0;   // 0,2 → image right | 1,3 → image left
        const isDarkSection = idx === 1 || idx === 3; // how-it-works + guardrails on dark
        const bgCls = isDarkSection ? "bg-slate-950 text-white" : "bg-white text-slate-900";
        const eyebrowCls = isDarkSection ? "text-white/70" : "text-slate-500";
        const proseCls = isDarkSection ? "text-slate-300" : "text-slate-700";
        const bulletCls = isDarkSection ? "text-slate-200" : "text-slate-800";
        const bulletDotBg = isDarkSection ? "bg-white/10" : accent.bg;
        const bulletDotFg = isDarkSection ? "text-white" : accent.text;
        const heading = s.heading(feat);
        const sub = s.subheading(feat);
        const proseLines = s.prose(feat);
        return (
          <section key={sectionKey} className={`${bgCls}`}
            data-testid={`studio-feature-section-${sectionKey}`}>
            <div className="max-w-6xl mx-auto px-6 py-20">
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14 items-center`}>
                <div className={`${rightSideImage ? "" : "md:order-2"}`}>
                  <div className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold ${eyebrowCls}`}>
                    <s.Icon className="w-3.5 h-3.5" /> {s.eyebrow}
                  </div>
                  <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                    {heading}
                  </h2>
                  <div className={`mt-2 text-base font-semibold ${isDarkSection ? "text-white/80" : accent.text}`}>
                    {sub}
                  </div>
                  <div className={`mt-5 space-y-3 ${proseCls}`}>
                    {proseLines.map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                  <ul className="mt-6 space-y-2">
                    {s.bullets.map((b, i) => (
                      <li key={i} className={`flex items-start gap-2.5 text-sm ${bulletCls}`}>
                        <span className={`mt-0.5 flex-shrink-0 inline-flex w-5 h-5 rounded-full items-center justify-center text-[11px] font-bold ${bulletDotBg} ${bulletDotFg}`}>
                          {i + 1}
                        </span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`${rightSideImage ? "" : "md:order-1"}`}>
                  <SectionInfographic
                    slug={slug}
                    section={sectionKey}
                    theme={isDarkSection ? "dark" : "dark"}
                    accent={feat.accent === "indigo" ? "#4F46E5" :
                            feat.accent === "violet" ? "#8B5CF6" :
                            feat.accent === "emerald" ? "#059669" :
                            feat.accent === "pink" ? "#EC4899" :
                            feat.accent === "sky" ? "#0EA5E9" :
                            feat.accent === "amber" ? "#F59E0B" :
                            feat.accent === "cyan" ? "#0891B2" : "#4F46E5"}
                    caption={`AI-generated · ${s.eyebrow.toLowerCase()} view`}
                  />
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Sub-feature grid — only on Studio home */}
      {isStudioHome && subfeatures.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-xs uppercase tracking-widest text-slate-600 font-bold">The 8 building blocks</div>
          <h2 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight">Everything the Studio does</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {subfeatures.map((s) => {
              const sub = STUDIO_FEATURES[s];
              if (!sub) return null;
              const sub_a = ACCENT_MAP[sub.accent] || ACCENT_MAP.indigo;
              return (
                <Link key={s} to={`/solutions/studio/${s}`}
                  data-testid={`studio-feature-card-${s}`}
                  className="group rounded-2xl border border-[#ebe4d3] bg-white p-5 hover:shadow-xl hover:-translate-y-0.5 transition-all">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${sub_a.grad} grid place-items-center text-white shadow-md`}>
                    <sub.Icon className="w-5 h-5" />
                  </div>
                  <div className="mt-3 text-base font-bold text-slate-900">{sub.title}</div>
                  <div className="mt-1 text-sm text-slate-600 line-clamp-3">{sub.tagline}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-slate-500 group-hover:text-indigo-700">
                    Read more <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Cross-links back for sub-feature pages */}
      {!isStudioHome && (
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="rounded-2xl border border-[#ebe4d3] bg-white p-6 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Part of</div>
              <div className="text-lg font-bold text-slate-900">AI Ad &amp; Growth Studio</div>
              <div className="text-sm text-slate-600">See the full workflow — brand DNA to campaigns to attribution.</div>
            </div>
            <Link to="/solutions/studio/ad-growth-studio"
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-bold">
              Open the Studio overview <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="mt-6 text-xs uppercase tracking-widest text-slate-500 font-bold">Other Studio blocks</div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
            {STUDIO_FAMILY_ORDER.filter((s) => s !== slug && s !== "ad-growth-studio").map((s) => (
              <Link key={s} to={`/solutions/studio/${s}`}
                className="text-sm px-3 py-2 rounded-lg border border-[#ebe4d3] bg-white hover:bg-[#f5efdd] text-slate-800 font-semibold">
                {STUDIO_FEATURES[s].title}
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
